import { useEffect, useMemo } from 'react';
import { useAppSelector } from '../redux/helper';
import type { OrderDoc } from '../redux/backoffice/ordersSlice';
import type { LocationObject } from '../redux/locationSlice';
import {
  selectLocationSwitching,
  selectValidCurrentLocation,
} from '../redux/locationSlice';
import {
  selectActivationTrackerPhase,
  selectOwnerSetupWizardStatus,
} from '../redux/ownerActivationSlice';
import {
  useOwnerActivationBootstrap,
  type OwnerActivationBootstrapEvidence,
} from './useOwnerActivationBootstrap';

/**
 * Home-side bootstrap reconciliation (US5, T078).
 *
 * After successful Home data fetches (products + recent transactions) it maps
 * verified existing data to activation completion evidence and feeds the
 * monotonic bootstrap. It only ever reports from a resolved current location
 * and from succeeded (not loading / not errored) fetches, so a returning owner
 * with pre-existing products/sales/test-location has those tasks credited
 * without duplicating what the wizard/checkout already dispatched.
 */

const isCompletedRealSale = (doc: OrderDoc | undefined): boolean => {
  if (!doc) return false;
  const status = String(doc.bp_order_master_order_status || '').toLowerCase();
  return status === 'completed';
};

export interface OwnerHomeActivationEvidenceInput {
  currentLocation: Pick<
    LocationObject,
    '_id' | 'bp_location_is_system_generated'
  > | null;
  /** POS products for the current location (already location-scoped). */
  productCount: number;
  /** Products fetch succeeded (not loading, not errored). */
  productsLoaded: boolean;
  recentTransactionDocs: OrderDoc[];
  /** Recent transactions fetch succeeded (not loading, not errored). */
  transactionsLoaded: boolean;
  verifiedAt: string;
}

/**
 * Pure evidence derivation, exported for direct unit testing. add_staff and
 * explore_backoffice are never derivable on Home and are omitted.
 */
export const deriveOwnerHomeActivationEvidence = (
  input: OwnerHomeActivationEvidenceInput,
): OwnerActivationBootstrapEvidence[] => {
  const {
    currentLocation,
    productCount,
    productsLoaded,
    recentTransactionDocs,
    transactionsLoaded,
    verifiedAt,
  } = input;

  const locationId = currentLocation?._id;
  if (!locationId) return [];

  const isTest = currentLocation?.bp_location_is_system_generated === true;
  const evidence: OwnerActivationBootstrapEvidence[] = [];

  if (isTest) {
    evidence.push({
      taskId: 'visit_test_location',
      verifiedAt,
      currentLocationIsTest: true,
      locationId,
    });
    // On a test location, real product/sale evidence cannot be attributed here.
    return evidence;
  }

  if (productsLoaded && productCount > 0) {
    evidence.push({
      taskId: 'add_product',
      verifiedAt,
      hasRealLocationProduct: true,
      locationId,
    });
  }

  if (transactionsLoaded && recentTransactionDocs.some(isCompletedRealSale)) {
    evidence.push({
      taskId: 'first_sale',
      verifiedAt,
      hasCompletedRealLocationSale: true,
      locationId,
    });
  }

  return evidence;
};

export interface UseOwnerHomeActivationBootstrapParams {
  productCount: number;
  productsLoaded: boolean;
  recentTransactionDocs: OrderDoc[];
  transactionsLoaded: boolean;
}

export const useOwnerHomeActivationBootstrap = ({
  productCount,
  productsLoaded,
  recentTransactionDocs,
  transactionsLoaded,
}: UseOwnerHomeActivationBootstrapParams): void => {
  const wizardStatus = useAppSelector(selectOwnerSetupWizardStatus);
  const phase = useAppSelector(selectActivationTrackerPhase);
  const currentLocation = useAppSelector(selectValidCurrentLocation);
  const isLocationSwitching = useAppSelector(selectLocationSwitching);
  const { reportBootstrapEvidence } = useOwnerActivationBootstrap();

  const locationId = currentLocation?._id ?? null;
  const isTest = currentLocation?.bp_location_is_system_generated === true;
  const hasCompletedRealSale = useMemo(
    () => recentTransactionDocs.some(isCompletedRealSale),
    [recentTransactionDocs],
  );

  // Enable rules from location-scoping plan §5:
  // 1. Wizard not terminal → disabled
  const wizardTerminal = wizardStatus === 'completed' || wizardStatus === 'skipped';
  // 2. Location switching → disabled
  // 3. No current location → disabled
  // 4. Test location: allow ONLY visit_test_location (no phase requirement)
  // 5. Real location: require phase active | completed_unacknowledged

  useEffect(() => {
    // Rule 1: wizard not terminal
    if (!wizardTerminal) return;
    // Rule 2: location switching
    if (isLocationSwitching) return;
    // Rule 3: no current location
    if (!locationId) return;

    // Rule 4: Test location — only allow visit_test_location evidence
    if (isTest) {
      reportBootstrapEvidence([
        {
          taskId: 'visit_test_location',
          verifiedAt: new Date().toISOString(),
          currentLocationIsTest: true,
          locationId,
        },
      ]);
      return;
    }

    // Rule 5: Real location — require phase active/completed_unacknowledged
    const realEnabled = phase === 'active' || phase === 'completed_unacknowledged';
    if (!realEnabled) return;

    const evidence = deriveOwnerHomeActivationEvidence({
      currentLocation,
      productCount,
      productsLoaded,
      recentTransactionDocs,
      transactionsLoaded,
      verifiedAt: new Date().toISOString(),
    });
    if (evidence.length > 0) {
      reportBootstrapEvidence(evidence);
    }
    // Derived booleans/ids keep this stable across doc-array identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    wizardTerminal,
    phase,
    locationId,
    isTest,
    isLocationSwitching,
    productCount,
    productsLoaded,
    hasCompletedRealSale,
    transactionsLoaded,
    reportBootstrapEvidence,
  ]);
};

export default useOwnerHomeActivationBootstrap;
